import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { CheckCircle2, ChevronRight } from '@/components/icons';
import { ToastContext, type StepToastOpts, type Toast, type ToastType } from './ToastContext';
import { fireNotification } from '@/lib/notify';

const TYPE_CLASS: Record<ToastType, string> = {
  success: 'border-l-success',
  error: 'border-l-danger',
  info: 'border-l-info',
  warning: 'border-l-warning',
};

const TYPE_ICON: Record<ToastType, string> = {
  success: '✓',
  error: '✗',
  info: 'ℹ',
  warning: '⚠',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextIdRef = useRef(1);

  const push = useCallback((message: string, type: ToastType = 'info') => {
    const id = nextIdRef.current++;
    setToasts((cur) => [...cur, { id, type, message }]);
    setTimeout(() => {
      setToasts((cur) => cur.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  const pushStep = useCallback((title: string, opts: StepToastOpts = {}) => {
    const id = nextIdRef.current++;
    setToasts((cur) => [
      ...cur,
      {
        id,
        type: 'success',
        message: title,
        step: {
          body: opts.body,
          href: opts.href,
          nextLabel: opts.nextLabel,
        },
      },
    ]);
    // Step toasts stay a bit longer so the user can click the next-step link.
    setTimeout(() => {
      setToasts((cur) => cur.filter((t) => t.id !== id));
    }, 6000);

    if (opts.browserNotification !== false) {
      fireNotification(title, {
        body: opts.body,
        href: opts.href,
        tag: opts.tag,
      });
    }
  }, []);

  const value = useMemo(() => ({ push, pushStep }), [push, pushStep]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-5 top-20 z-[9999] flex flex-col gap-2">
        {toasts.map((t) =>
          t.step ? (
            <StepToastCard key={t.id} toast={t} />
          ) : (
            <div
              key={t.id}
              className={clsx(
                'flex min-w-[280px] items-center gap-2.5 rounded-lg border border-[var(--border-color)] border-l-4 bg-[var(--bg-secondary)] px-4 py-3 text-sm font-bold shadow-lg',
                TYPE_CLASS[t.type],
              )}
            >
              <span className="text-base">{TYPE_ICON[t.type]}</span>
              <span>{t.message}</span>
            </div>
          ),
        )}
      </div>
    </ToastContext.Provider>
  );
}

function StepToastCard({ toast }: { toast: Toast }) {
  const step = toast.step!;
  const clickable = !!step.href;
  const inner = (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex-shrink-0 rounded-full bg-success/20 p-1.5 text-success">
        <CheckCircle2 size={16} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-extrabold text-[var(--text-primary)]">{toast.message}</div>
        {step.body && (
          <div className="mt-0.5 text-xs text-[var(--text-secondary)]">{step.body}</div>
        )}
        {clickable && (
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-accent">
            {step.nextLabel ?? 'Continue'}
            <ChevronRight size={12} strokeWidth={2.5} />
          </div>
        )}
      </div>
    </div>
  );
  const className =
    'block min-w-[320px] max-w-[380px] rounded-lg border border-l-4 border-l-success border-[var(--border-color)] bg-[var(--bg-secondary)] p-3.5 text-left shadow-xl ' +
    (clickable ? 'cursor-pointer transition hover:border-accent/60 hover:bg-accent/[0.04]' : '');
  return clickable ? (
    <a href={step.href} className={className}>
      {inner}
    </a>
  ) : (
    <div className={className}>{inner}</div>
  );
}
